/**
 * What is currently moving.
 *
 * DESIGN INTENT
 *   This should almost always be empty, and that is the point. Because storage
 *   is content-addressed, most uploads finish before a progress bar would have
 *   drawn its first frame — so a file the workspace already holds appears as a
 *   quiet "already here" line, not as a bar that snaps to full.
 *
 *   The tray only appears when something is genuinely in flight or has failed.
 *   A panel that is always on screen showing nothing is clutter.
 */
import { useEffect, useState } from 'react'
import { ArrowDownToLine, ArrowUpFromLine, Check, X, Zap, AlertCircle, ChevronDown } from 'lucide-react'
import { useTransfers, useTransferSummary, cancelTransfer } from '@/store/transfers'
import { isTerminal, wasInstant, type Transfer } from '@/lib/transfer'
import { Button, Spinner, Tooltip } from './ui/primitives'
import { formatBytes, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'

/** Keep a finished tray on screen briefly, then let it go. */
const LINGER_MS = 4000

export function TransferTray() {
  const items = useTransfers(s => s.items)
  const clearFinished = useTransfers(s => s.clearFinished)
  const summary = useTransferSummary()
  const [collapsed, setCollapsed] = useState(false)

  // Once everything has settled, tidy up on its own.
  useEffect(() => {
    if (items.length === 0 || summary.busy) return
    const timer = setTimeout(() => { if (!useTransfers.getState().items.some(i => !isTerminal(i.phase))) clearFinished() }, LINGER_MS)
    return () => clearTimeout(timer)
  }, [items, summary.busy, clearFinished])

  if (items.length === 0) return null

  const failed = items.filter(i => i.phase === 'failed')

  return (
    <div className="pop-in fixed bottom-3 right-3 z-40 w-80 overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-raised)] shadow-modal">
      <header className="flex h-9 items-center gap-2 border-b border-[var(--border)] px-2.5">
        {summary.busy ? <Spinner size={13} className="text-[var(--accent)]" />
          : failed.length ? <AlertCircle className="h-3.5 w-3.5 text-[var(--deleted)]" strokeWidth={1.75} />
          : <Check className="h-3.5 w-3.5 text-[var(--added)]" strokeWidth={2} />}

        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {summary.busy
            ? `${pluralise(summary.active, 'transfer')} · ${summary.percent}%`
            : failed.length
              ? `${pluralise(failed.length, 'transfer')} failed`
              : summary.skipped > 0 && summary.done === 0
                ? `${pluralise(summary.skipped, 'file')} already here`
                : `${pluralise(summary.done + summary.skipped, 'file')} done`}
        </span>

        <button
          onClick={() => setCollapsed(c => !c)}
          className="snap rounded p-0.5 text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)]"
          aria-label={collapsed ? 'Expand transfers' : 'Collapse transfers'}
        >
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', collapsed && 'rotate-180')} strokeWidth={2} />
        </button>
        <button
          onClick={clearFinished}
          className="snap rounded p-0.5 text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)]"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </header>

      {summary.busy && (
        <div className="h-0.5 bg-[var(--bg-inset)]" role="progressbar" aria-valuenow={summary.percent}>
          <div className="h-full bg-[var(--accent)] transition-[width] duration-200" style={{ width: `${summary.percent}%` }} />
        </div>
      )}

      {!collapsed && (
        <ul className="max-h-64 overflow-y-auto p-1">
          {items.slice(0, 60).map(item => <Row key={item.id} transfer={item} />)}
          {items.length > 60 && (
            <li className="px-2 py-1 text-[11px] text-[var(--fg-subtle)]">…and {items.length - 60} more</li>
          )}
        </ul>
      )}
    </div>
  )
}

function Row({ transfer }: { transfer: Transfer }) {
  const done = isTerminal(transfer.phase)
  const instant = wasInstant(transfer)
  const percent = transfer.totalBytes > 0
    ? Math.min(100, Math.round((transfer.bytesDone / transfer.totalBytes) * 100))
    : 0

  return (
    <li className="flex items-center gap-2 rounded-[var(--radius-sm)] px-1.5 py-1 text-sm">
      {instant ? (
        <Tooltip label="Already in the workspace — nothing was uploaded">
          <Zap className="h-3.5 w-3.5 shrink-0 text-[var(--modified)]" strokeWidth={1.75} />
        </Tooltip>
      ) : transfer.phase === 'failed' ? (
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-[var(--deleted)]" strokeWidth={1.75} />
      ) : done ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-[var(--added)]" strokeWidth={2} />
      ) : transfer.direction === 'upload' ? (
        <ArrowUpFromLine className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
      ) : (
        <ArrowDownToLine className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate" title={transfer.path || transfer.name}>{transfer.name}</span>
        <span className="block truncate text-[11px] text-[var(--fg-subtle)]">
          {transfer.phase === 'failed' ? transfer.error
            : instant ? 'already here'
            : done ? formatBytes(transfer.totalBytes)
            : `${transfer.phase} · ${percent}%`}
        </span>
      </span>

      {!done && (
        <button
          onClick={() => cancelTransfer(transfer.id)}
          className="snap rounded p-0.5 text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)]"
          aria-label={`Cancel ${transfer.name}`}
        >
          <X className="h-3 w-3" strokeWidth={2} />
        </button>
      )}
    </li>
  )
}

export { Button }
